/* ============================================================
   nodeTexture.js — paints an "elegant floating card" for a single
   business module onto a canvas, returned for use as a three.js
   CanvasTexture on a node plane.
   ============================================================ */

const TAU = Math.PI * 2;

function roundRect(ctx, x, y, w, h, r) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

/* Minimal line-glyph per module, drawn in a 48x48 box at (gx,gy). */
function drawGlyph(ctx, kind, gx, gy, s, color) {
  ctx.save();
  ctx.translate(gx, gy);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = s * 0.06;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  const u = s; // unit box size
  switch (kind) {
    case "finance": // coin / dollar
      ctx.beginPath();
      ctx.arc(u * 0.5, u * 0.5, u * 0.38, 0, TAU);
      ctx.stroke();
      ctx.font = `700 ${u * 0.5}px 'Segoe UI', Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("$", u * 0.5, u * 0.54);
      break;
    case "hr": // person
      ctx.beginPath();
      ctx.arc(u * 0.5, u * 0.34, u * 0.16, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(u * 0.22, u * 0.82);
      ctx.quadraticCurveTo(u * 0.5, u * 0.5, u * 0.78, u * 0.82);
      ctx.stroke();
      break;
    case "sales": // upward arrow trend
      ctx.beginPath();
      ctx.moveTo(u * 0.18, u * 0.7);
      ctx.lineTo(u * 0.42, u * 0.46);
      ctx.lineTo(u * 0.56, u * 0.58);
      ctx.lineTo(u * 0.82, u * 0.3);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(u * 0.66, u * 0.3);
      ctx.lineTo(u * 0.82, u * 0.3);
      ctx.lineTo(u * 0.82, u * 0.46);
      ctx.stroke();
      break;
    case "operations": // gear-ish
      ctx.beginPath();
      ctx.arc(u * 0.5, u * 0.5, u * 0.2, 0, TAU);
      ctx.stroke();
      for (let i = 0; i < 8; i += 1) {
        const a = (i / 8) * TAU;
        ctx.beginPath();
        ctx.moveTo(u * 0.5 + Math.cos(a) * u * 0.26, u * 0.5 + Math.sin(a) * u * 0.26);
        ctx.lineTo(u * 0.5 + Math.cos(a) * u * 0.36, u * 0.5 + Math.sin(a) * u * 0.36);
        ctx.stroke();
      }
      break;
    case "inventory": // boxes
      ctx.strokeRect(u * 0.2, u * 0.42, u * 0.26, u * 0.26);
      ctx.strokeRect(u * 0.52, u * 0.42, u * 0.26, u * 0.26);
      ctx.strokeRect(u * 0.36, u * 0.16, u * 0.26, u * 0.26);
      break;
    case "procurement": // cart
      ctx.beginPath();
      ctx.moveTo(u * 0.18, u * 0.24);
      ctx.lineTo(u * 0.3, u * 0.24);
      ctx.lineTo(u * 0.4, u * 0.62);
      ctx.lineTo(u * 0.76, u * 0.62);
      ctx.lineTo(u * 0.82, u * 0.34);
      ctx.lineTo(u * 0.34, u * 0.34);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(u * 0.44, u * 0.74, u * 0.06, 0, TAU);
      ctx.arc(u * 0.72, u * 0.74, u * 0.06, 0, TAU);
      ctx.fill();
      break;
    case "analytics": // bars
    default:
      ctx.fillRect(u * 0.22, u * 0.5, u * 0.12, u * 0.28);
      ctx.fillRect(u * 0.44, u * 0.36, u * 0.12, u * 0.42);
      ctx.fillRect(u * 0.66, u * 0.24, u * 0.12, u * 0.54);
      break;
  }
  ctx.restore();
}

/**
 * Paint a module card. Returns the canvas (caller wraps in CanvasTexture).
 * @param {string} label   module name
 * @param {string} kind    glyph key (finance|hr|sales|operations|inventory|procurement|analytics)
 * @param {string} accent  accent color (brand)
 * @param {number} [scale] device-pixel multiplier for crispness
 */
export function makeNodeCanvas(label, kind, accent, scale = 4) {
  // Full canvas includes margin M around the card so the outer glow isn't clipped.
  const M = 28;
  const CW = 320; // card width
  const CH = 150; // card height
  const W = CW + M * 2;
  const H = CH + M * 2;
  const canvas = document.createElement("canvas");
  canvas.width = W * scale;
  canvas.height = H * scale;
  const ctx = canvas.getContext("2d");
  ctx.scale(scale, scale);
  ctx.textBaseline = "middle";

  const cx = M; // card origin x
  const cy = M; // card origin y

  // green outer glow around the glass card (no border). Two passes build a
  // soft, even halo around every card — same for all modules.
  ctx.save();
  ctx.shadowColor = "rgba(43,183,143,0.85)"; // brand green/teal glow
  ctx.shadowBlur = 100;
  roundRect(ctx, cx, cy, CW, CH, 26);
  ctx.fillStyle = "rgba(11,171,100,0.20)";
  ctx.fill();
  ctx.shadowBlur = 18;
  ctx.fill();
  ctx.restore();

  // frosted glass card body (lighter, more translucent than before)
  roundRect(ctx, cx, cy, CW, CH, 26);
  const g = ctx.createLinearGradient(cx, cy, cx, cy + CH);
  g.addColorStop(0, "rgba(38,56,88,0.78)");
  g.addColorStop(1, "rgba(16,28,48,0.82)");
  ctx.fillStyle = g;
  ctx.fill();
  // frosted top sheen
  roundRect(ctx, cx, cy, CW, CH * 0.5, 26);
  ctx.fillStyle = "rgba(255,255,255,0.05)";
  ctx.fill();
  // (border removed — the green outer glow defines the card edge)

  // glyph badge (neutral-tinted)
  const bx = cx + 22;
  const by = cy + CH / 2 - 34;
  roundRect(ctx, bx, by, 68, 68, 18);
  ctx.fillStyle = "rgba(255,255,255,0.08)";
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = "rgba(255,255,255,0.22)";
  ctx.stroke();
  drawGlyph(ctx, kind, bx + 10, by + 10, 48, "#ffffff");

  // label — crisp bright white with a thin dark outline (no blur)
  const tx = cx + 108;
  const midY = cy + CH / 2;
  ctx.textAlign = "left";
  ctx.lineJoin = "round";
  ctx.font = "800 31px 'Segoe UI', Arial, sans-serif";
  const drawLabel = (txt, yy) => {
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(6,14,26,0.6)"; // crisp dark edge for contrast (not a blur)
    ctx.strokeText(txt, tx, yy);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(txt, tx, yy);
  };
  const words = label.split(" ");
  if (words.length > 1 && ctx.measureText(label).width > CW - 132) {
    const mid = Math.ceil(words.length / 2);
    drawLabel(words.slice(0, mid).join(" "), midY - 16);
    drawLabel(words.slice(mid).join(" "), midY + 16);
  } else {
    drawLabel(label, midY - 2);
  }

  // tiny "module" tag
  ctx.fillStyle = "rgba(210,224,240,0.95)";
  ctx.font = "700 13px 'Segoe UI', Arial, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("MODULE", tx, midY + 44);

  return canvas;
}

/* The 7 ecosystem modules, in display order. */
export const MODULES = [
  { label: "Financial Accounting", kind: "finance", accent: "#6fe0b4" },
  { label: "Human Resources", kind: "hr", accent: "#3bb78f" },
  { label: "Sales", kind: "sales", accent: "#6fe0b4" },
  { label: "Operations", kind: "operations", accent: "#0bab64" },
  { label: "Inventory", kind: "inventory", accent: "#3bb78f" },
  { label: "Procurement", kind: "procurement", accent: "#6fe0b4" },
  { label: "Analytics", kind: "analytics", accent: "#0bab64" },
];
